import React, { useState, useEffect } from "react";
import TakafulDataService from "../services/TakafulService";
import UserDataService from "../services/UserService";
import { useParams, useNavigate } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Stack,
  Button,
  CircularProgress,
  Snackbar,
  Alert
} from "@mui/material";
import { formatWithCommas, formatToTwoDecimals } from "../../utils/formatNumber";

const toDateInput = (d) => {
  const dt = d ? new Date(d) : new Date();
  const yyyy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, "0");
  const dd = String(dt.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const AddTakaful = () => {
  const { owner } = useParams();
  const navigate = useNavigate();
  const initialTakafulState = {
    owner: owner || "",
    date: toDateInput(new Date()),
    amount: "",
    creditOrDebit: "credit",
    description: "",
    createdAt: null,
    createdBy: null,
    updatedAt: null,
    updatedBy: null
  };

  const [takaful, setTakaful] = useState(initialTakafulState);
  const [ownerDisplay, setOwnerDisplay] = useState(() => (UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : ""));
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!owner) return;
    if (ownerDisplay) return;
    const cached = UserDataService.getCachedUserName ? UserDataService.getCachedUserName(owner) : "";
    if (cached) {
      setOwnerDisplay(cached);
      return;
    }
    let mounted = true;
    UserDataService.get(owner)
      .then(res => {
        const u = res?.data ?? res ?? {};
        const fname = u.firstName || u.firstname || u.first_name || u.name || "";
        const lname = u.lastName || u.lastname || u.last_name || "";
        const fullName = (lname ? lname + (fname ? " " : "") : "") + fname;
        if (mounted && fullName) setOwnerDisplay(fullName);
      })
      .catch(() => {});
    return () => { mounted = false; };
  }, [owner, ownerDisplay]);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    if (name === "amount") {
      setTakaful(prev => ({ ...prev, amount: formatWithCommas(value) }));
    } else {
      setTakaful(prev => ({ ...prev, [name]: value }));
    }
  };

  const saveTakaful = async () => {
    setError("");
    if (!takaful.amount || Number.isNaN(Number(String(takaful.amount).replace(/,/g, "")))) {
      setError("Please enter a valid amount");
      return;
    }
    if (!takaful.creditOrDebit) {
      setError("Please select transaction type");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        owner: takaful.owner,
        date: typeof takaful.date === "string" ? takaful.date : toDateInput(takaful.date),
        amount: Number(String(takaful.amount).replace(/,/g, "")),
        creditOrDebit: String(takaful.creditOrDebit),
        description: (takaful.description || "").trim()
      };

      const res = await TakafulDataService.create(takaful.owner, payload);
      const r = res?.data || {};

      setTakaful({
        owner: r.owner ?? takaful.owner,
        date: r.date ?? payload.date,
        amount: r.amount ?? payload.amount,
        creditOrDebit: r.creditOrDebit ?? payload.creditOrDebit,
        description: r.description ?? payload.description,
        createdAt: r.createdAt ?? null,
        createdBy: r.createdBy ?? null,
        updatedAt: r.updatedAt ?? null,
        updatedBy: r.updatedBy ?? null
      });

      navigate(`/dashboard/takaful/${encodeURIComponent(r.owner ?? takaful.owner)}`);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || e?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const newTakaful = () => {
    setTakaful({ ...initialTakafulState, owner: owner || initialTakafulState.owner });
    setSubmitted(false);
    setError("");
    if (!owner) setOwnerDisplay("");
  };

  return (
    <Paper elevation={3} sx={{ maxWidth: 840, margin: "24px auto", p: { xs: 2, md: 4 } }}>
      <Typography variant="h6" gutterBottom>
        {submitted ? "Transaction saved" : "Add Takaful Transaction"}
      </Typography>

      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {submitted ? (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" color="primary" onClick={newTakaful}>
              Add Another
            </Button>
          </Stack>
        </Box>
      ) : (
        <Box component="form" noValidate autoComplete="off" sx={{ mt: 1 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                id="owner"
                name="owner"
                label="Owner"
                value={ownerDisplay || ""}
                fullWidth
                InputProps={{ readOnly: true }}
                helperText={owner ? "Owner provided via route (read-only)" : "Owner (read-only)"}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="date"
                name="date"
                label="Date of Deposit"
                helperText="Date deposit occurred (system creation timestamp is recorded automatically)"
                type="date"
                value={takaful.date}
                onChange={handleInputChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="amount"
                name="amount"
                label="Amount"
                type="text"
                placeholder="e.g. 4,000,000.00"
                value={takaful.amount}
                onChange={handleInputChange}
                onBlur={() => {
                  if (takaful.amount) {
                    setTakaful(prev => ({ ...prev, amount: formatToTwoDecimals(prev.amount) }));
                  }
                }}
                fullWidth
                InputProps={{ inputMode: "numeric" }}
                required
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2">Transaction Type</Typography>
                <Button
                  variant={takaful.creditOrDebit === "credit" ? "contained" : "outlined"}
                  onClick={() => setTakaful(s => ({ ...s, creditOrDebit: "credit" }))}
                >
                  Credit
                </Button>
                <Button
                  variant={takaful.creditOrDebit === "debit" ? "contained" : "outlined"}
                  onClick={() => setTakaful(s => ({ ...s, creditOrDebit: "debit" }))}
                >
                  Debit
                </Button>
              </Stack>
            </Grid>

            <Grid item xs={12}>
              <TextField
                id="description"
                name="description"
                label="Description (optional)"
                value={takaful.description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
                placeholder="Enter a brief description of the transaction"
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={saveTakaful}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={18} /> : null}
                >
                  Submit
                </Button>
                <Button variant="outlined" onClick={newTakaful} disabled={loading}>
                  Reset
                </Button>
                <Button variant="text" onClick={() => window.history.back()}>
                  Cancel
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      )}

      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setSnackOpen(false)} severity="success" sx={{ width: "100%" }}>
          Transaction saved successfully
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default AddTakaful;
